import React, { useMemo, useState, useEffect } from 'react';
import toast from 'react-hot-toast';
import { useStore } from '../store/useStore';
import { setSheetCalibration } from '../utils/api';
import {
  SCALE_PRESETS,
  LENGTH_FORMATS,
  DISTANCE_UNITS,
  ppfFromPreset,
  ppfFromTwoPoint,
  distance,
} from '../utils/measure';
import styles from './CalibrationModal.module.css';

// Two-tab modal — Preset scale OR Two-point calibration. The two-point tab has
// its own picking flow: user clicks "Start picking", modal closes, user clicks
// two points on the sheet (MarkupCanvas collects them), then the store auto-
// reopens this modal with the points pre-filled for distance entry.
export default function CalibrationModal() {
  const {
    activeSheet, setActiveSheetCalibration,
    setCalibrationModalOpen,
    calibrationPickPoints, resetCalibrationPick,
    setActiveTool,
  } = useStore();

  const existing = activeSheet?.calibration || null;

  // If the user returns from picking with 2 points, default to the two-point tab.
  const [tab, setTab] = useState(() =>
    calibrationPickPoints.length === 2
      ? 'two-point'
      : existing?.method === 'two-point' ? 'two-point' : 'preset'
  );

  // ── Shared format state ──────────────────────────────────────────────────
  const [lengthFormat, setLengthFormat] = useState(existing?.lengthFormat || 'architectural');

  // ── Preset tab state ─────────────────────────────────────────────────────
  const presetLabelFromExisting = existing?.method === 'preset' ? existing.label : '';
  const [presetLabel, setPresetLabel] = useState(presetLabelFromExisting || '1/4" = 1\'-0"');

  // ── Two-point tab state ──────────────────────────────────────────────────
  const [realValue, setRealValue] = useState(
    existing?.reference?.realValue != null ? String(existing.reference.realValue) : ''
  );
  const [realUnit, setRealUnit] = useState(existing?.reference?.realUnit || 'ft');

  const [saving, setSaving] = useState(false);

  // Group presets for the dropdown.
  const groupedPresets = useMemo(() => {
    const g = {};
    SCALE_PRESETS.forEach((p) => { (g[p.group] = g[p.group] || []).push(p); });
    return g;
  }, []);

  // Distance in page-points between the two picked points (if any).
  const pickedPageDist = useMemo(() => {
    if (calibrationPickPoints.length !== 2) return 0;
    return distance(calibrationPickPoints[0], calibrationPickPoints[1]);
  }, [calibrationPickPoints]);

  // If picks get cleared while open, drop back to preset tab automatically.
  useEffect(() => {
    if (tab === 'two-point' && calibrationPickPoints.length === 0 && existing?.method !== 'two-point') {
      // keep user where they are; they may want to pick again
    }
  }, [tab, calibrationPickPoints, existing]);

  function close() {
    setCalibrationModalOpen(false);
  }

  function startPicking() {
    resetCalibrationPick();
    setActiveTool('calibrate');
    setCalibrationModalOpen(false);
  }

  async function applyPreset() {
    const preset = SCALE_PRESETS.find((p) => p.label === presetLabel);
    if (!preset) { toast.error('Pick a scale first'); return; }
    const ppf = ppfFromPreset(preset.paperInPerFoot);
    const cal = {
      pagePointsPerFoot: ppf,
      lengthFormat,
      method: 'preset',
      label: preset.label,
      setAt: new Date().toISOString(),
      setBy: useStore.getState().currentUser?.name || 'unknown',
    };
    await save(cal);
  }

  async function applyTwoPoint() {
    if (calibrationPickPoints.length !== 2) { toast.error('Pick two points on the sheet first'); return; }
    const v = parseFloat(realValue);
    if (!v || v <= 0) { toast.error('Enter a positive distance'); return; }
    const ppf = ppfFromTwoPoint(pickedPageDist, v, realUnit);
    if (!ppf) { toast.error('Invalid calibration'); return; }
    const [p1, p2] = calibrationPickPoints;
    const unitShort = realUnit;
    const cal = {
      pagePointsPerFoot: ppf,
      lengthFormat,
      method: 'two-point',
      label: `${v} ${unitShort} reference`,
      reference: {
        x1: p1.x, y1: p1.y, x2: p2.x, y2: p2.y,
        realValue: v, realUnit: unitShort,
      },
      setAt: new Date().toISOString(),
      setBy: useStore.getState().currentUser?.name || 'unknown',
    };
    await save(cal);
  }

  async function save(cal) {
    if (!activeSheet) return;
    setSaving(true);
    try {
      await setSheetCalibration(activeSheet.id, cal);
      setActiveSheetCalibration(cal);
      resetCalibrationPick();
      toast.success('Calibration saved');
      close();
    } catch (err) {
      toast.error(err.message || 'Save failed');
    } finally {
      setSaving(false);
    }
  }

  async function clearCalibration() {
    if (!activeSheet) return;
    setSaving(true);
    try {
      await setSheetCalibration(activeSheet.id, null);
      setActiveSheetCalibration(null);
      resetCalibrationPick();
      toast.success('Calibration cleared');
      close();
    } catch (err) {
      toast.error(err.message || 'Clear failed');
    } finally {
      setSaving(false);
    }
  }

  return (
    <div
      className={styles.backdrop}
      onClick={(e) => e.target === e.currentTarget && close()}
    >
      <div className={styles.modal}>
        <div className={styles.header}>
          <span className={styles.title}>CALIBRATE SCALE</span>
          <button className={styles.closeBtn} onClick={close}>×</button>
        </div>

        <div className={styles.tabs}>
          <button
            className={`${styles.tab} ${tab === 'preset' ? styles.tabActive : ''}`}
            onClick={() => setTab('preset')}
          >
            Preset scale
          </button>
          <button
            className={`${styles.tab} ${tab === 'two-point' ? styles.tabActive : ''}`}
            onClick={() => setTab('two-point')}
          >
            Two-point
          </button>
        </div>

        <div className={styles.body}>
          {existing && (
            <div className={styles.currentRow}>
              <span className={styles.currentLabel}>CURRENT</span>
              <span className={styles.currentValue}>{existing.label || 'Calibrated'}</span>
              <span className={styles.currentMeta}>
                {existing.method === 'two-point' ? '2-pt' : 'preset'} · {existing.lengthFormat}
              </span>
            </div>
          )}

          {tab === 'preset' && (
            <section className={styles.section}>
              <label className={styles.fieldLabel}>Drawing scale</label>
              <select
                className={styles.select}
                value={presetLabel}
                onChange={(e) => setPresetLabel(e.target.value)}
              >
                {Object.entries(groupedPresets).map(([group, presets]) => (
                  <optgroup key={group} label={group}>
                    {presets.map((p) => (
                      <option key={p.label} value={p.label}>{p.label}</option>
                    ))}
                  </optgroup>
                ))}
              </select>
              <p className={styles.hint}>
                Pick the scale printed on the sheet. This maps PDF page-points to real feet.
              </p>
            </section>
          )}

          {tab === 'two-point' && (
            <section className={styles.section}>
              {calibrationPickPoints.length < 2 ? (
                <>
                  <p className={styles.hint}>
                    Click two points on the drawing that are a known distance apart (e.g. a
                    dimensioned wall). Then enter that real-world distance here.
                  </p>
                  <button
                    className={styles.pickBtn}
                    onClick={startPicking}
                  >
                    Start picking points
                  </button>
                </>
              ) : (
                <>
                  <div className={styles.pickedRow}>
                    <span className={styles.pickedLabel}>PICKED</span>
                    <span className={styles.pickedValue}>
                      {pickedPageDist.toFixed(2)} pt on sheet
                    </span>
                    <button className={styles.repick} onClick={startPicking}>
                      Re-pick
                    </button>
                  </div>

                  <div className={styles.distanceRow}>
                    <div className={styles.distanceField}>
                      <label className={styles.fieldLabel}>Real distance</label>
                      <input
                        type="number"
                        min="0"
                        step="any"
                        className={styles.input}
                        value={realValue}
                        onChange={(e) => setRealValue(e.target.value)}
                        placeholder="e.g. 10"
                        autoFocus
                      />
                    </div>
                    <div className={styles.unitField}>
                      <label className={styles.fieldLabel}>Unit</label>
                      <select
                        className={styles.select}
                        value={realUnit}
                        onChange={(e) => setRealUnit(e.target.value)}
                      >
                        {DISTANCE_UNITS.map((u) => (
                          <option key={u.id} value={u.id}>{u.label}</option>
                        ))}
                      </select>
                    </div>
                  </div>
                </>
              )}
            </section>
          )}

          <section className={styles.section}>
            <label className={styles.fieldLabel}>Length format</label>
            <div className={styles.formatGrid}>
              {LENGTH_FORMATS.map((f) => (
                <label
                  key={f.id}
                  className={`${styles.formatCard} ${lengthFormat === f.id ? styles.formatActive : ''}`}
                >
                  <input
                    type="radio"
                    name="length-format"
                    value={f.id}
                    checked={lengthFormat === f.id}
                    onChange={() => setLengthFormat(f.id)}
                  />
                  <div className={styles.formatName}>{f.label}</div>
                  <div className={styles.formatSample}>{f.sample}</div>
                </label>
              ))}
            </div>
            <p className={styles.hint}>
              Areas always show sq ft (+ sq yd); volumes always show cu ft (+ cu yd).
            </p>
          </section>
        </div>

        <div className={styles.footer}>
          {existing && (
            <button
              className={styles.clearBtn}
              onClick={clearCalibration}
              disabled={saving}
            >
              Clear
            </button>
          )}
          <div className={styles.footerSpacer} />
          <button className={styles.cancelBtn} onClick={close} disabled={saving}>
            Cancel
          </button>
          {tab === 'preset' ? (
            <button
              className={styles.submitBtn}
              onClick={applyPreset}
              disabled={saving}
            >
              {saving ? 'Saving…' : 'Apply scale'}
            </button>
          ) : (
            <button
              className={styles.submitBtn}
              onClick={applyTwoPoint}
              disabled={saving || calibrationPickPoints.length !== 2 || !realValue}
            >
              {saving ? 'Saving…' : 'Apply calibration'}
            </button>
          )}
        </div>
      </div>
    </div>
  );
}
